import React from 'react';

import { FormProvider, Controller, useForm } from 'react-hook-form';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { Toast } from 'src/common/components';
import { useAuth } from 'src/common/hooks';

import { LANGUAGE_OPTION, TIMEZONE_OPTION, INDUSTRY, EMAIL_REGEX, HOST } from '../../constants';
import { IndustryType } from '../../types';
import { SelectStyle } from '../../utils';
import { createAccount, fetchIndustry } from '../api';
import { Action, SALESFORCE_CALENDAR, SALESFORCE_TOWN } from '../constants';
import { useCreateAccountContext } from '../context';
import { FormType } from '../types';

import SearchCampaignDetails from './SearchCampaignDetails';
import SearchLegalEntities from './SearchLegalEntities';
import SearchParentAccounts from './SearchParentAccounts';
import SearchSalesforceAccounts from './SearchSalesforceAccounts';

type CallbackType = (industries: IndustryType[]) => void;

const createParentAccountLink = `${HOST}/bh_staff/cosmos/parentaccount/add/`;
const createLegalEntityLink = `/app/tcr/legal_entities/create`;
const createCampaignDetailLink = `/app/tcr/campaign_details/create`;

const AccountInformation: React.FC = () => {
  const methods = useForm<FormType>();
  const {
    register,
    formState: { errors },
    control,
  } = methods;
  const { connectedUser } = useAuth();
  const navigate = useNavigate();
  const { dispatch } = useCreateAccountContext();

  const getIndustry = (_: string, callback: CallbackType): void => {
    fetchIndustry()
      .then(({ data }) => {
        callback(data);
      })
      .catch(() => callback([]));
  };

  const handleNavigate = (): void => {
    navigate('/accounts');
  };

  const [addAccount, { isLoading }] = useMutation(createAccount, {
    onSuccess: (response) => {
      const account = response.data;
      dispatch({
        type: Action.SET_STEP,
        account,
      });
    },
    onError: () => {
      Toast.error('There was an error creating the account.');
    },
  });

  const onSubmit = (data: FormType): void => {
    const { timezone, lang, industry, calendar_name, salesforce_id, parent_account } = data;
    const payload = {
      ...data,
      parent_account,
      lang: lang.value,
      timezone: timezone.value,
      company_industry: industry.id,
      salesforce_id: salesforce_id || null,
      calendar_name: calendar_name.value,
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    addAccount(payload);
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onSubmit)}>
        {!connectedUser.is_reseller && <SearchSalesforceAccounts />}
        <SearchParentAccounts />
        <SearchLegalEntities />
        <SearchCampaignDetails />
        <h3 className="mt-8">Account information</h3>
        <div className="md:grid-cols-5 gap-y-6 grid grid-cols-1 mt-6">
          <div className="md:col-span-3">
            <label htmlFor="salesforce_id" className="input-label">
              Salesforce Account ID
              <input
                id="salesforce_id"
                className="input w-full mt-1 transition duration-150 ease-in-out"
                {...register('salesforce_id')}
                defaultValue=""
                data-testid="salesforce-id"
                autoComplete="off"
              />
              <p className="text-xs font-semibold text-orange-1.0">
                This field gets pre-filled if you search for a Salesforce account and select it.
                Leave empty if you do NOT want to attach a Salesforce account to the Cosmos account.
              </p>
            </label>
          </div>
          <div className="md:col-span-3">
            <label htmlFor="parent_account" className="input-label">
              Parent Account ID
              <input
                id="parent_account"
                className="input w-full mt-1 transition duration-150 ease-in-out"
                {...register('parent_account')}
                defaultValue=""
                data-testid="parent-account-id"
                autoComplete="off"
              />
              <p className="text-xs font-semibold text-orange-1.0">
                This field gets pre-filled when you search for a Parent Account and select it. All
                Accounts created via the Admin Portal should be linked to a Parent Account. If this
                is an Account for a new Customer they should go through the Registration Flow so
                that we can capture their payment information.&nbsp;
                <a
                  className="text-blue-1.0"
                  target="_blank"
                  rel="noreferrer"
                  href={createParentAccountLink}
                >
                  (Create a new Parent Account)
                </a>
              </p>
              {errors.parent_account && (
                <p className="error-text"> The parent account is required</p>
              )}
            </label>
          </div>
          <div className="md:col-span-3">
            <label htmlFor="legal_entity" className="input-label">
              Legal Entity ID
              <input
                id="legal_entity"
                className="input w-full mt-1 transition duration-150 ease-in-out"
                {...register('legal_entity')}
                defaultValue=""
                data-testid="legal-entity-id"
                autoComplete="off"
              />
              <p className="text-xs font-semibold text-orange-1.0">
                This field gets pre-filled when you search for a Legal Entity and select it.&nbsp;
                <a
                  className="text-blue-1.0"
                  target="_blank"
                  rel="noreferrer"
                  href={createLegalEntityLink}
                >
                  (Create a new Legal Entity)
                </a>
              </p>
              {errors.legal_entity && <p className="error-text"> The legal entity is required</p>}
            </label>
          </div>
          <div className="md:col-span-3">
            <label htmlFor="campaign_detail" className="input-label">
              Campaign Detail ID
              <input
                id="campaign_detail"
                className="input w-full mt-1 transition duration-150 ease-in-out"
                {...register('campaign_detail')}
                defaultValue=""
                data-testid="campaign-detail-id"
                autoComplete="off"
              />
              <p className="text-xs font-semibold text-orange-1.0">
                This field gets pre-filled when you search for a Campaign Detail and select
                it.&nbsp;
                <a
                  className="text-blue-1.0"
                  target="_blank"
                  rel="noreferrer"
                  href={createCampaignDetailLink}
                >
                  (Create a new Campaign Detail)
                </a>
              </p>
              {errors.campaign_detail && (
                <p className="error-text"> The campaign detail is required</p>
              )}
            </label>
          </div>
          <div className="md:col-span-3">
            <label htmlFor="business_name" className="input-label">
              Business name
              <input
                id="name"
                className="input w-full mt-1 transition duration-150 ease-in-out"
                {...register('name', { required: true })}
                defaultValue=""
                data-testid="account-name"
                autoComplete="off"
              />
              <p className="text-xs font-semibold text-gray-1.0">
                Name of the account. This is shown in the scheduling link, the website and is used
                as a signature in some messages.
              </p>
              {errors.name && <p className="error-text"> The name is required</p>}
            </label>
          </div>
          <div className="md:col-span-3">
            <label htmlFor="email" className="input-label">
              Email
              <input
                id="email"
                type="text"
                className="input w-full mt-1 transition duration-150 ease-in-out"
                {...register('email', {
                  required: 'The email is required',
                  pattern: {
                    value: EMAIL_REGEX,
                    message: 'Entered value does not match email format',
                  },
                })}
                defaultValue=""
                data-testid="account-email"
                autoComplete="off"
              />
              {errors.email && <p className="error-text">{errors.email?.message}</p>}
            </label>
          </div>
          <div className="md:col-span-3">
            <label htmlFor="business_phone" className="input-label">
              Business phone
              <input
                id="office_phone"
                type="phone"
                className="input w-full mt-1 transition duration-150 ease-in-out"
                {...register('office_phone', { required: true })}
                defaultValue=""
                data-testid="account-phone"
                autoComplete="off"
              />
              {errors.office_phone && <p className="error-text"> The phone is required</p>}
            </label>
          </div>
          <div className="md:col-span-3">
            <p className="input-label">Language</p>
            <Controller
              name="lang"
              render={({ field }) => (
                <Select
                  styles={SelectStyle}
                  aria-label="Language"
                  {...field}
                  placeholder=""
                  options={LANGUAGE_OPTION}
                />
              )}
              control={control}
              rules={{ required: true }}
              data-testid="account-language"
            />
            {errors.lang && <p className="error-text"> The language is required</p>}
          </div>
          <div className="md:col-span-3">
            <p className="input-label">Timezone</p>
            <Controller
              name="timezone"
              render={({ field }) => (
                <Select
                  styles={SelectStyle}
                  aria-label="Timezone"
                  {...field}
                  placeholder=""
                  options={TIMEZONE_OPTION}
                />
              )}
              control={control}
              rules={{ required: true }}
            />
            {errors.timezone && <p className="error-text"> The timezone is required</p>}
          </div>
          <div className="md:col-span-3">
            <p className="input-label">Industry</p>
            <Controller
              name="industry"
              render={({ field }) => (
                <AsyncSelect
                  placeholder=""
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  styles={SelectStyle}
                  aria-label="Industry"
                  cacheOptions
                  defaultOptions
                  loadOptions={getIndustry}
                  getOptionLabel={(option: IndustryType) => option.name}
                  getOptionValue={(option: IndustryType) => option.id}
                  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  options={INDUSTRY}
                  {...field}
                />
              )}
              control={control}
              rules={{ required: true }}
            />
            {errors.industry && <p className="error-text"> The industry is required</p>}
          </div>
          <div className="md:col-span-3">
            <p className="input-label">Calendar name</p>
            <Controller
              name="calendar_name"
              render={({ field }) => (
                <Select
                  options={SALESFORCE_CALENDAR}
                  styles={SelectStyle}
                  aria-label="Calendar name"
                  {...field}
                  placeholder=""
                />
              )}
              control={control}
              rules={{ required: true }}
            />
            <p className="text-xs font-semibold text-gray-1.0">
              Select the &quot;BrainHi&quot; calendar to create the account as a standalone account.
            </p>
            {errors.calendar_name && <p className="error-text"> The calendar name is required</p>}
          </div>
          <div className="md:col-span-3">
            <label htmlFor="username" className="input-label">
              Username
              <input
                id="username"
                type="text"
                className="input w-full mt-1 transition duration-150 ease-in-out"
                {...register('username')}
                defaultValue=""
                data-testid="username"
                autoComplete="off"
              />
              <p className="text-xs text-gray-1.0 font-semibold">
                Enter a username to create a new user. A password will then be generated and sent to
                the email specified above. Leave empty if you do NOT want a new user to be created.
              </p>
            </label>
          </div>
          {connectedUser.is_reseller && (
            <>
              <div className="md:col-span-3">
                <p className="input-label">Town</p>
                <Controller
                  name="town"
                  render={({ field }) => (
                    <Select
                      options={SALESFORCE_TOWN}
                      styles={SelectStyle}
                      aria-label="Town"
                      placeholder=""
                      {...field}
                    />
                  )}
                  control={control}
                  rules={{ required: true }}
                />
                {errors.town && <p className="error-text"> The town is required</p>}
              </div>
              <div className="md:col-span-3">
                <label htmlFor="notes" className="input-label">
                  Notes
                  <textarea
                    id="notes"
                    rows={6}
                    className="input w-full mt-1 transition duration-150 ease-in-out"
                    {...register('notes', { required: true })}
                    defaultValue=""
                    data-testid="account-phone"
                  />
                  {errors.notes && <p className="error-text"> Notes are required</p>}
                </label>
              </div>
            </>
          )}
        </div>
        <div className="mt-8 border-t border-white-2.0" />
        <div className="flex justify-end flex-shrink-0 mt-6">
          <button type="button" className="btn btn-ghost" onClick={handleNavigate}>
            Cancel
          </button>
          <button type="submit" className="btn btn-secondary ml-3" data-testid="account-submit">
            {isLoading ? 'Saving...' : 'Save'}
          </button>
        </div>
      </form>
    </FormProvider>
  );
};

export default AccountInformation;
